/* eslint-disable import/no-unused-modules */
import { PressableNeolexon } from '@/components/PressableNeolexon';
import colors from '@/config/colors';
import FontAwesome from '@expo/vector-icons/FontAwesome';
import { useVideoPlayer, VideoView } from 'expo-video';
import React, { useCallback, useEffect, useState } from 'react';
import { ActivityIndicator, LayoutChangeEvent, View } from 'react-native';
import { useEventListener } from 'expo';
import styles from './style';
import { setAudioModeAsync } from 'expo-audio';
import { logError } from '@/utils/logHandling';
import { useAutoAdvance } from '@/hooks/useAutoAdvance';
import { toWebAssetUrl } from '@/utils/webAssetUrl';

interface VideoPlayerProps {
  url?: string;
  noStyles?: boolean;
  noVideo: boolean;
  correctSelected: boolean;
  handleIsVideoPlaying?: (value: boolean) => void;
  isRecording?: boolean;
  isAudioPlaying?: boolean;
  autoPlay?: boolean;
  onCorrect: (value?: any) => void;
  playbackRate?: number;
  children?: React.ReactElement;
}

export function VideoPlayer({
  url,
  noVideo,
  noStyles,
  correctSelected,
  handleIsVideoPlaying,
  isRecording,
  isAudioPlaying,
  autoPlay,
  onCorrect,
  playbackRate = 1.0,
  children,
}: VideoPlayerProps) {
  const [noVideoState, setNoVideoState] = useState<boolean>(noVideo);
  const [containerSize, setContainerSize] = useState<number>(1);
  const [isPlaying, setIsPlayingState] = useState<boolean>(false);
  const disabled = isRecording || isAudioPlaying;

  const { onMediaFinished } = useAutoAdvance({
    correctSelected,
    onCorrect,
    getDurationAndPosition: () => ({
      duration: player?.duration ?? 0,
      position: player?.currentTime ?? 0,
    }),
    onBeforeAdvance: () => {
      handleIsVideoPlaying?.(false);
    },
    resetKey: url,
  });

  const player = useVideoPlayer({
    uri: toWebAssetUrl(url),
  });
  useEventListener(player, 'playToEnd', () => {
    handleVideoFinish();
  });

  useEventListener(player, 'playingChange', (payload) => {
    setIsPlayingState(payload.isPlaying);
  });

  useEventListener(player, 'statusChange', ({ status, error }) => {
    if (status === 'error') {
      logError('Video playback failed', error, { url });
    }
  });

  useEffect(() => {
    if (correctSelected && !isAudioPlaying) {
      // Hack: On iOS, the video player does not play sound if it is started immediately after a sound has been played.
      // This is because the audio session is still in "recording" mode. To fix this, we wait 100ms before starting the video playback.
      setTimeout(playAudioOnly, 100);
    }
  }, [correctSelected, isAudioPlaying]);

  useEffect(() => {
    if (autoPlay) {
      playUrl();
    }
  }, []);

  const playUrl = useCallback(async () => {
    // The recording-playback player (expo-audio) and this player (expo-video)
    // are separate native modules sharing one iOS AVAudioSession. When a
    // feedback is tapped mid-playback, the audio player is paused in the same
    // commit this player starts, leaving the session routed/attenuated so the
    // video plays silently (isPlaying=true, muted=false, but no sound).
    // Awaiting a fresh playback mode both re-activates the session and defers
    // play() past the audio pause/seek so the two no longer race.
    try {
      await setAudioModeAsync({
        allowsRecording: false,
        playsInSilentMode: true,
      });
    } catch (error) {
      logError('Could not set audio mode before video playback', error);
    }

    player.playbackRate = correctSelected ? 1 : playbackRate;
    player.preservesPitch = true;
    player.currentTime = 0;
    player.play();

    if (handleIsVideoPlaying) {
      handleIsVideoPlaying(true);
    }
  }, [correctSelected, playbackRate]);

  const handleVideoFinish = () => {
    if (handleIsVideoPlaying) {
      handleIsVideoPlaying(false);
    }

    if (correctSelected) {
      onMediaFinished();
    } else {
      player.currentTime = 0;
      player.pause();
    }
  };

  const playAudioOnly = useCallback(() => {
    setNoVideoState(true);
    playUrl();
  }, [correctSelected]);

  // Handle layout changes
  const onContainerLayout = (event: LayoutChangeEvent) => {
    const { width, height } = event.nativeEvent.layout;
    const size = Math.min(width, height);
    setContainerSize(size);
  };

  return (
    <View style={[styles.wrapper]} onLayout={onContainerLayout}>
      <View
        style={[
          { width: containerSize, height: containerSize },
          noStyles ? {} : styles.videoContainer,
        ]}
      >
        {isPlaying ? (
          <>
            {noVideoState ? (
              <View
                style={
                  children
                    ? {}
                    : [
                        styles.audioStyles,
                        {
                          width: containerSize,
                          height: containerSize,
                          borderRadius: containerSize / 2,
                        },
                      ]
                }
              >
                {children ? (
                  children
                ) : (
                  <FontAwesome
                    name={'volume-up'}
                    size={containerSize * 0.8}
                    color={colors.white}
                  />
                )}
              </View>
            ) : (
              <View style={[styles.videoContainer]}>
                {player.bufferedPosition <= -1 &&
                  player.status === 'loading' && (
                    <View
                      style={[
                        {
                          width: containerSize,
                          height: containerSize,
                          position: 'absolute',
                          zIndex: 50,
                        },
                        styles.center,
                      ]}
                    >
                      <ActivityIndicator
                        size="large"
                        color={colors.darkPurpleBlue}
                      />
                    </View>
                  )}
              </View>
            )}
          </>
        ) : (
          <PressableNeolexon
            onPress={() => playUrl()}
            disabled={disabled}
            style={[
              styles.questionStyles,
              {
                width: containerSize,
                height: containerSize,
                borderRadius: containerSize / 2,
              },
              disabled && styles.disabled,
            ]}
          >
            <FontAwesome
              name={'question'}
              size={containerSize * 0.8}
              color={colors.white}
              solid
            />
          </PressableNeolexon>
        )}

        <VideoView
          key={1}
          style={
            isPlaying && !noVideoState
              ? {
                  width: containerSize,
                  height: containerSize,
                }
              : {
                  width: 0,
                  height: 0,
                  opacity: 0,
                }
          }
          player={player}
          allowsPictureInPicture={false}
          nativeControls={false}
        />
      </View>
    </View>
  );
}
